import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';
import SuccessMessage from '../../components/successMessage';
import IncomesModal from './incomesModal';
import ReceiptModal from './receiptModal';
import FinancialResult from '../FinancialResult';
import { useIncomesScreen, incomeStatusConfig } from '../hooks/useIncomesScreen';

export default function IncomesMobile() {
  const {
    incomes,
    totalAmount,
    totalAmountToBeReceived,
    totalAmountReceived,
    isLoading,
    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,
    menuAnchorEl,
    menuSelectedIncome,
    openMenu,
    closeMenu,
    openIncomeModal,
    setOpenIncomeModal,
    openReceiptModal,
    setOpenReceiptModal,
    selectedIncomeId,
    addIncome,
    editIncome,
    receive,
    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,
    saveSuccess,
    saveError,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useIncomesScreen();

  return (
    <Box>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1.5 }}>
        Entradas
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <FinancialResult selectedDate={selectedDate} compact />
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
        <IconButton onClick={goToPreviousMonth} aria-label="Mês anterior">
          <ChevronLeft />
        </IconButton>
        <DatePicker
          label="mês e ano"
          views={['month', 'year']}
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          slotProps={{ textField: { fullWidth: true } }}
        />
        <IconButton onClick={goToNextMonth} aria-label="Próximo mês">
          <ChevronRight />
        </IconButton>
      </Stack>

      <Button
        data-cy="btn-add-income"
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={addIncome}
        sx={{ mb: 2 }}
      >
        Adicionar Entrada
      </Button>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        {incomes.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Nenhuma entrada encontrada para o período.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {incomes.map((income, index) => (
              <React.Fragment key={income.id}>
                <ListItem
                  sx={{ display: 'block', py: 1.5, px: 2 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="ações da entrada"
                      onClick={(event) => openMenu(event, income)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  }
                >
                  <Box sx={{ pr: 4 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
                      {income.description}
                    </Typography>

                    {income.observation && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}
                      >
                        {income.observation}
                      </Typography>
                    )}

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        label={income.status}
                        color={incomeStatusConfig[income.status].color}
                        variant={incomeStatusConfig[income.status].variant}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Typography variant="body2" fontWeight="medium">
                        {income.amount}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      Vence em {income.dueDate}
                      {income.receiptMethodType ? ` · ${income.receiptMethodType}` : ''}
                    </Typography>
                  </Box>
                </ListItem>
                {index < incomes.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>

      {incomes.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Total: <strong>{totalAmount}</strong>
          </Typography>
          <Typography variant="caption" color="success.main">
            Total Recebido: <strong>{totalAmountReceived}</strong>
          </Typography>
          <Typography variant="caption" color="warning.main">
            Total a Receber: <strong>{totalAmountToBeReceived}</strong>
          </Typography>
        </Stack>
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={editIncome}>Editar</MenuItem>
        <MenuItem onClick={receive} disabled={menuSelectedIncome?.status === 'Recebida'}>
          Receber
        </MenuItem>
        <MenuItem onClick={clickDelete} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      {openIncomeModal && (
        <IncomesModal
          open={openIncomeModal}
          onClose={() => setOpenIncomeModal(false)}
          onSaveSuccess={saveSuccess}
          incomeId={selectedIncomeId}
          incomeOrigin={'FINANCIAL'}
          patientId={null}
        />
      )}

      {openReceiptModal && (
        <ReceiptModal
          open={openReceiptModal}
          onClose={() => setOpenReceiptModal(false)}
          onSaveSuccess={saveSuccess}
          onSaveError={saveError}
          incomeId={selectedIncomeId}
        />
      )}

      <Dialog open={openDeleteModal} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Tem certeza que deseja excluir essa entrada?
            <br />
            Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="primary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="outlined" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
